import React, {
  createContext,
  useState,
  useMemo,
  useCallback,
  PropsWithChildren,
  useEffect,
} from 'react';
import omit from 'lodash/omit';
import uniq from 'lodash/uniq';
import noop from 'lodash/noop';
import {
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  MultiMediaResponse,
} from 'dtos';
import {
  getProfileImages,
} from 'pages/Dashboard/utils/helper';
import {
  MediaOriginatorType,
} from 'pages/Dashboard/utils/constants';
import isNil from 'lodash/isNil';

type MediaContextProps = {
  getMediaPathById: (id: number, originatorType?: MediaOriginatorType) => string | null;
  getMediaById: (id: number, originatorType?: MediaOriginatorType) => MultiMediaResponse | null;
  requestMedia: (ids: number[], originatorType?: MediaOriginatorType) => void;
  invalidateMedia: (id: number, originatorType?: MediaOriginatorType) => void;
  isFetching: (originatorType?: MediaOriginatorType, id?: number) => boolean;
};

export const MediaContext = createContext<MediaContextProps>({
  getMediaById: () => null,
  getMediaPathById: () => null,
  requestMedia: noop,
  invalidateMedia: noop,
  isFetching: () => false,
});

type MediaProviderProps = PropsWithChildren<{
  canUseMedia?: boolean;
}>;

type MediaByType = Record<string, Record<number, MultiMediaResponse>>;
type IdsByType = Record<string, number[]>;

const QUERY_KEYS: Partial<Record<MediaOriginatorType, string>> = {
  [MediaOriginatorType.Patient]: 'patient-media',
  [MediaOriginatorType.User]: 'user-media',
};

export default function MediaProvider({
  children,
  canUseMedia = true,
}: MediaProviderProps) {
  const [mediaByType, setMediaByType] = useState<MediaByType>({
    [MediaOriginatorType.Patient]: {},
    [MediaOriginatorType.User]: {},
  });
  const [idsByType, setIdsByType] = useState<IdsByType>({
    [MediaOriginatorType.Patient]: [],
    [MediaOriginatorType.User]: [],
  });

  const patientIds = idsByType[MediaOriginatorType.Patient] ?? [];
  const userIds = idsByType[MediaOriginatorType.User] ?? [];

  const {
    data: patientData = [],
    isFetching: isFetchingPatientMedia,
  } = useQuery<MultiMediaResponse[], Error>({
    queryKey: [QUERY_KEYS[MediaOriginatorType.Patient], JSON.stringify(patientIds)],
    queryFn: () => getProfileImages(MediaOriginatorType.Patient, patientIds),
    enabled: canUseMedia && patientIds.length > 0,
  });

  const {
    data: userData = [],
    isFetching: isFetchingUserMedia,
  } = useQuery<MultiMediaResponse[], Error>({
    queryKey: [QUERY_KEYS[MediaOriginatorType.User], JSON.stringify(userIds)],
    queryFn: () => getProfileImages(MediaOriginatorType.User, userIds),
    enabled: canUseMedia && userIds.length > 0,
  });

  useEffect(() => {
    if (!canUseMedia) {
      setIdsByType({
        [MediaOriginatorType.Patient]: [],
        [MediaOriginatorType.User]: [],
      });
    }
  }, [canUseMedia]);

  useEffect(() => {
    if (patientData?.length > 0) {
      const list: Record<number, MultiMediaResponse> = {};
      patientData.forEach((mediaResponse: MultiMediaResponse) => {
        if (mediaResponse?.originatorId) {
          list[mediaResponse.originatorId] = mediaResponse;
        }
      });
      setMediaByType((prev) => ({
        ...prev,
        [MediaOriginatorType.Patient]: { ...(prev[MediaOriginatorType.Patient] ?? {}), ...list },
      }));
    }
  }, [patientData]);

  useEffect(() => {
    if (userData?.length > 0) {
      const list: Record<number, MultiMediaResponse> = {};
      userData.forEach((mediaResponse: MultiMediaResponse) => {
        if (mediaResponse?.originatorId) {
          list[mediaResponse.originatorId] = mediaResponse;
        }
      });
      setMediaByType((prev) => ({
        ...prev,
        [MediaOriginatorType.User]: { ...(prev[MediaOriginatorType.User] ?? {}), ...list },
      }));
    }
  }, [userData]);

  const getMediaPathById = useCallback((
    id: number,
    originatorType: MediaOriginatorType = MediaOriginatorType.Patient,
  ) => (
    mediaByType[originatorType]?.[id]?.url?.value ?? null
  ), [mediaByType]);

  const getMediaById = useCallback((
    id: number,
    originatorType: MediaOriginatorType = MediaOriginatorType.Patient,
  ) => (
    mediaByType[originatorType]?.[id] ?? null
  ), [mediaByType]);

  const requestMedia = useCallback((
    ids: number[],
    originatorType: MediaOriginatorType = MediaOriginatorType.Patient,
  ) => {
    if (!canUseMedia) return;
    const validIds = uniq(ids.filter((id) => id > 0));
    if (validIds.length > 0) {
      setIdsByType((prev) => {
        const currentIds = prev[originatorType] ?? [];
        const currentMedia = mediaByType[originatorType] ?? {};
        const newIds = validIds.filter((id) => !currentIds.includes(id) && !(id in currentMedia));

        return newIds.length === 0 ? prev : {
          ...prev,
          [originatorType]: uniq([...currentIds, ...newIds]),
        };
      });
    }
  }, [canUseMedia, mediaByType]);

  const queryClient = useQueryClient();

  const invalidateMedia = useCallback((
    id: number,
    originatorType: MediaOriginatorType = MediaOriginatorType.Patient,
  ) => {
    setMediaByType((prev) => ({
      ...prev,
      [originatorType]: omit(prev[originatorType] ?? {}, [id]),
    }));
    setIdsByType((prev) => ({
      ...prev,
      [originatorType]: uniq([...(prev[originatorType] ?? []), id]),
    }));

    const queryKey = QUERY_KEYS[originatorType] ?? QUERY_KEYS[MediaOriginatorType.Patient];
    queryClient.invalidateQueries({ queryKey: [queryKey] });
    queryClient.refetchQueries({ queryKey: [queryKey] });
  }, [queryClient]);

  const isFetching = useCallback((
    originatorType: MediaOriginatorType = MediaOriginatorType.Patient,
    id?: number,
  ) => {
    const isFetchingType = originatorType === MediaOriginatorType.User
      ? isFetchingUserMedia
      : isFetchingPatientMedia;

    if (!isNil(id) && id > 0) {
      const currentIds = idsByType[originatorType] ?? [];
      return isFetchingType && currentIds.includes(id);
    }

    return isFetchingType;
  }, [isFetchingPatientMedia, isFetchingUserMedia, idsByType]);

  const value = useMemo(() => ({
    getMediaById,
    getMediaPathById,
    requestMedia,
    invalidateMedia,
    isFetching,
  }), [
    getMediaById,
    getMediaPathById,
    requestMedia,
    invalidateMedia,
    isFetching,
  ]);

  return (
    <MediaContext.Provider value={value}>
      {children}
    </MediaContext.Provider>
  );
}
